/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { CircularProgress, Grid } from '@material-ui/core';
import { format } from 'date-fns';
import {
  DefaultButton,
  FakeInput,
  MessageBox,
  PhotoCard,
  TextField,
  useFormHook,
} from 'design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

// Analytics
import {
  EMISSION_CAR_INFORMATION_STEP,
  trackCurrentStep,
  trackEvent,
} from 'src/analytics';

// Constant
import DEFAULT_SERIAL_MOTOR from 'src/constants/serialMotor';

// Data Layer
import { ANNULMENT_STATUS } from 'data-layer-cr/dist/functions/carPolicies';
import { useAlreadyInsuredNewCarCheck } from 'data-layer-cr/dist/hooks';
import {
  setRequestStatus,
  useNetworkObserver,
} from 'data-layer-cr/dist/network';
import NetworkClient from 'src/services/NetworkClient';

// Redux
import {
  setCarInvoiceBase64,
  setExclusionDate,
  updateEmissionByPage,
} from 'src/store/emissionPolicy/actions';
import {
  REQUEST_UPDATE_EMISSION_DATA,
  REQUEST_UPLOAD_EMISSION_FILES,
} from 'src/store/emissionPolicy/requestIdentifiers';
import { setTextValues } from 'src/store/inspectionForm/actions';
import { TextValuesPayload } from 'src/store/inspectionForm/slice/inspectionFormSlice';
import { RootState } from 'src/store/rootReducer';

import { InstructionsCard } from 'src/components/general';

// Functions
import {
  QuotationUnit,
  RequestIndicatorTypes,
} from 'data-layer-cr/dist/models';
import QuotationProfile from 'data-layer-cr/dist/models/QuotationProfile';
import profiles from 'src/constants/profileTypes';
import isAlphaNumeric from 'src/functions/isAlphaNumeric';
import { REQUEST_QUOTATION } from 'src/store/quotation/requestIdentifiers';
import makeValidationSchema from './makeValidationSchema';

// Components
import useBaseWrapperStyles from '../useBaseWrapperStyles';

import { AlreadyInsuredErrorDialogue } from '../../components';

type StaticRowProps = {
  label: string;
  value: string;
};

export function makeStaticRow({ label, value }: StaticRowProps): JSX.Element {
  return (
    <Grid item xs={12}>
      <FakeInput inputLabel={label} defaultValue={value} />
    </Grid>
  );
}

type FormShape = {
  licensePlate: string;
  vinNumber: string;
};

export const vinNetworkError =
  'No se pudo verificar el vin, por favor intente nuevamente';

export const licensePlateNetworkError =
  'No se pudo verificar la placa, por favor intente nuevamente';

const useSubmitData = (
  setTextValuesAction: (payload: TextValuesPayload) => void,
  textValues: TextValuesPayload,
  requestUpdateEmission: (page: number) => void,
) => {
  return (data: FormShape): void => {
    setTextValuesAction({
      ...textValues,
      licensePlate: data.licensePlate,
      vinNumber: data.vinNumber,
      serialMotor: DEFAULT_SERIAL_MOTOR,
    });
    requestUpdateEmission(3);
  };
};

type LicensePlateAndVinSectionProps = {
  quotationProfile: QuotationProfile;
  normalizedLicensePlate: string;
  licensePlateValue: string;
  licensePlateRequestStatus: RequestIndicatorTypes;
  licensePlateValidationError: boolean;
  licensePlateErrorMessage: string;
  licensePlateChange: (val: string) => void;
  normalizedSerialChassis: string;
  vinNumberValue: string;
  vinRequestStatus: RequestIndicatorTypes;
  vinNumberValidationError: boolean;
  hasActivePolicy: boolean;
  mostRecentPolicySource: string;
  vinErrorMessage: string;
  vinChange: (val: string) => void;
};

export const LicensePlateAndVinSection = ({
  quotationProfile,
  normalizedLicensePlate,
  licensePlateValue,
  licensePlateRequestStatus,
  licensePlateValidationError,
  licensePlateErrorMessage,
  licensePlateChange,
  normalizedSerialChassis,
  vinNumberValue,
  vinRequestStatus,
  vinNumberValidationError,
  hasActivePolicy,
  mostRecentPolicySource,
  vinErrorMessage,
  vinChange,
}: LicensePlateAndVinSectionProps) => {
  return (
    <Grid item xs={12} md={6}>
      <InstructionsCard
        title={
          quotationProfile.type !== profiles.RETENCION
            ? 'VIN o número de Chasis'
            : 'Datos del auto'
        }
        question="¿Dónde está mi VIN o No. de chasis?"
        answer="Lo puede encontrar en el Registro Vehicular o en estos 4 lugares en el Auto"
        dialogTitle="¿Dónde está mi VIN?"
        imagePath="images/vin_instructions.svg"
      >
        <Grid container spacing={1}>
          {quotationProfile.type === profiles.RETENCION &&
            (normalizedLicensePlate !== '' ? (
              makeStaticRow({ label: 'Placa', value: licensePlateValue })
            ) : (
              <Grid item xs={12}>
                <TextField
                  inputId="sales-web-inspection-input-license"
                  inputLabel="Placa"
                  inputName="licensePlate"
                  loading={licensePlateRequestStatus === 'requesting'}
                  defaultValue={licensePlateValue}
                  inputError={licensePlateValidationError}
                  inputErrorMessage={licensePlateErrorMessage}
                  onChange={(val): void =>
                    licensePlateChange(val.toUpperCase())
                  }
                  shouldUpdate={isAlphaNumeric}
                />
              </Grid>
            ))}

          {normalizedSerialChassis !== '' ? (
            makeStaticRow({ label: 'VIN', value: vinNumberValue })
          ) : (
            <Grid item xs={12}>
              <TextField
                inputId="sales-web-inspection-input-vin"
                inputLabel="VIN"
                inputName="vinNumber"
                loading={vinRequestStatus === 'requesting'}
                defaultValue={vinNumberValue}
                inputError={
                  vinNumberValidationError ||
                  (hasActivePolicy && mostRecentPolicySource === 'vinNumber')
                }
                inputErrorMessage={vinErrorMessage}
                onChange={(val): void => vinChange(val.toUpperCase())}
                shouldUpdate={isAlphaNumeric}
              />
            </Grid>
          )}
        </Grid>
      </InstructionsCard>
    </Grid>
  );
};

type NewCarInvoiceSectionProps = {
  quotationProfile: QuotationProfile;
  newCarInvoiceBase64: string;
  newCarInvoiceFilename: string;
  setCarInvoiceBase64Action: (fileName: string, base64Image: string) => void;
  invoiceFileStatus: RequestIndicatorTypes;
};

export const NewCarInvoiceSection = ({
  quotationProfile,
  newCarInvoiceBase64,
  newCarInvoiceFilename,
  setCarInvoiceBase64Action,
  invoiceFileStatus,
}: NewCarInvoiceSectionProps) => {
  return (
    <Grid item xs={12} md={6}>
      <PhotoCard
        id="sales-web-inspection-button-proformacar"
        data-testid="photo-card-invoice"
        title={
          quotationProfile.type !== profiles.RETENCION
            ? 'Factura del vehículo'
            : 'Certificado póliza actual'
        }
        subTitle={
          quotationProfile.type !== profiles.RETENCION
            ? 'Incluye una foto o archivo PDF de la factura o proforma del vehículo'
            : 'Incluye una foto o archivo PDF del certificado de la póliza o documento alterno.'
        }
        onLocalImageCaptured={setCarInvoiceBase64Action}
        base64String={newCarInvoiceBase64}
        fileName={newCarInvoiceFilename}
        confirmationMessage="¿Estás seguro que deseas eliminar la tarjeta de circulación?"
        requestStatus={invoiceFileStatus}
      />
    </Grid>
  );
};

export const getInitVariables = (textValue?: string) => {
  return textValue ?? '';
};

export const NewCarData = ({
  newCarInvoiceFilename,
  newCarInvoiceBase64,
  textValues,
  setTextValuesAction,
  setCarInvoiceBase64Action,
  setExclusionDateAction,
  onSubmit,
  requestUpdateEmission,
  resetRequestStatus,
  quotationUnits,
  quotationProfile,
}: Props): JSX.Element => {
  const classes = useBaseWrapperStyles();
  const [alreadyInsuredDialogueOpen, toggleAlreadyInsuredDialogueOpen] =
    useState(false);
  const invoiceFileStatus = useNetworkObserver(REQUEST_UPLOAD_EMISSION_FILES);
  const requestingUpdateDataStatus = useNetworkObserver(
    REQUEST_UPDATE_EMISSION_DATA,
  );

  const { licensePlate, serialChassis } = quotationUnits[0].unit || {};

  const normalizedLicensePlate = getInitVariables(licensePlate);
  const normalizedSerialChassis = getInitVariables(serialChassis);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (requestingUpdateDataStatus === 'success') {
      resetRequestStatus(REQUEST_UPDATE_EMISSION_DATA, 'initial');
      trackEvent({
        eventName: EMISSION_CAR_INFORMATION_STEP,
        eventProperties: {},
      });
      trackCurrentStep(EMISSION_CAR_INFORMATION_STEP);
      onSubmit();
    }
  }, [requestingUpdateDataStatus, onSubmit, resetRequestStatus]);

  const submitData = useSubmitData(
    setTextValuesAction,
    textValues,
    requestUpdateEmission,
  );

  const initialValues: FormShape = {
    licensePlate: licensePlate || textValues.licensePlate,
    vinNumber: serialChassis || textValues.vinNumber,
  };
  const { handleSubmit, providedAllRequired, errors, getInputProps } =
    useFormHook<FormShape>(
      initialValues,
      submitData,
      makeValidationSchema(quotationProfile.type),
    );

  const vinNumberValue = getInputProps('vinNumber').value as string;
  const licensePlateValue = getInputProps('licensePlate').value as string;

  /** Validation errors */
  const vinNumberValidationError = getInputProps('vinNumber').error;
  const vinErrorFromForm = getInputProps('vinNumber').errorText;
  const licensePlateValidationError = getInputProps('licensePlate').error;
  const licensePlateErrorFromForm = getInputProps('licensePlate').errorText;

  /** Change */
  const vinChange = getInputProps('vinNumber').onChange;
  const licensePlateChange = getInputProps('licensePlate').onChange;

  const onDialogueRequested = useCallback(
    () => toggleAlreadyInsuredDialogueOpen(true),
    [],
  );

  const {
    hasActivePolicy,
    vinRequestStatus,
    mostRecentCarPolicy,
    updatingData,
    mostRecentPolicySource,
  } = useAlreadyInsuredNewCarCheck({
    vinNumberValidationError,
    motorNumberValidationError: false,
    client: NetworkClient,
    vinNumber: vinNumberValue,
    motorNumber: vinNumberValue,
    onMostRecentPolicyFound: (): void => {},
    onSerialMotorMustReset: (): void => {},
  });

  useEffect(() => {
    if (hasActivePolicy && mostRecentPolicySource !== '') {
      onDialogueRequested();
    }
  }, [
    hasActivePolicy,
    mostRecentCarPolicy,
    mostRecentPolicySource,
    onDialogueRequested,
  ]);

  useEffect(() => {
    if (
      !hasActivePolicy &&
      ANNULMENT_STATUS.includes(mostRecentCarPolicy?.policyStatus as string)
    ) {
      const exlusionDate = new Date(
        mostRecentCarPolicy?.effectiveEndDate as string,
      );
      exlusionDate.setDate(exlusionDate.getDate() + 1);
      exlusionDate.setHours(0, 0, 0, 0);
      setExclusionDateAction(format(exlusionDate, 'dd/MM/yyyy'));
    }
  }, [hasActivePolicy, mostRecentCarPolicy, setExclusionDateAction]);

  const vinErrorMessage = useMemo(
    () => (vinRequestStatus === 'failure' ? vinNetworkError : vinErrorFromForm),
    [vinErrorFromForm, vinRequestStatus],
  );

  const licensePlateRequestStatus: RequestIndicatorTypes =
    useNetworkObserver(REQUEST_QUOTATION);

  const licensePlateErrorMessage = useMemo(
    () =>
      licensePlateRequestStatus === 'failure'
        ? licensePlateNetworkError
        : licensePlateErrorFromForm,
    [licensePlateErrorFromForm, licensePlateRequestStatus],
  );

  /**
   * This variable will be true if the "next" button needs to be disabled in the containing form
   */
  const dataIsInvalid =
    hasActivePolicy ||
    vinRequestStatus === 'failure' ||
    licensePlateRequestStatus === 'failure' ||
    (vinRequestStatus === 'initial' && vinNumberValue === '') ||
    updatingData;

  const buttonIsDisabled =
    !providedAllRequired() ||
    dataIsInvalid ||
    Object.keys(errors).length > 0 ||
    newCarInvoiceBase64 === '' ||
    [invoiceFileStatus].includes('requesting') ||
    ['requesting', 'success'].includes(requestingUpdateDataStatus);

  return (
    <form onSubmit={handleSubmit}>
      {/* Error dialogue to display when the car is already insured */}
      <AlreadyInsuredErrorDialogue
        data-testid="already-insured-dialogue"
        open={alreadyInsuredDialogueOpen}
        onCloseRequested={(): void => toggleAlreadyInsuredDialogueOpen(false)}
        source={mostRecentPolicySource}
        licensePlate={licensePlateValue}
        vinNumber={vinNumberValue}
        motorNumber={vinNumberValue}
      />
      <Grid container justifyContent="center">
        <Grid
          item
          xs={12}
          md={7}
          container
          spacing={2}
          justifyContent="center"
          className={classes.pageContainer}
        >
          <Grid item xs={12}>
            <MessageBox
              message={
                quotationProfile.type !== profiles.RETENCION ? (
                  <div>
                    Asegúrate que los datos de Placa y VIN (número de chasis)
                    estén correctos para evitar inconvenientes con el tránsito.
                  </div>
                ) : (
                  <div>
                    Debemos verificar tu póliza actual, asegurate de ingresar
                    los datos correctos.
                  </div>
                )
              }
              variant="info"
              hideCloseButton
            />
          </Grid>
          {/* LicensePlate & Vin section section */}
          <LicensePlateAndVinSection
            quotationProfile={quotationProfile}
            normalizedLicensePlate={normalizedLicensePlate}
            licensePlateValue={licensePlateValue}
            licensePlateRequestStatus={licensePlateRequestStatus}
            licensePlateValidationError={licensePlateValidationError}
            licensePlateErrorMessage={licensePlateErrorMessage}
            licensePlateChange={licensePlateChange}
            normalizedSerialChassis={normalizedSerialChassis}
            vinNumberValue={vinNumberValue}
            vinRequestStatus={vinRequestStatus}
            vinNumberValidationError={vinNumberValidationError}
            hasActivePolicy={hasActivePolicy}
            mostRecentPolicySource={mostRecentPolicySource}
            vinErrorMessage={vinErrorMessage}
            vinChange={vinChange}
          />

          {/* New car invoice section */}
          <NewCarInvoiceSection
            quotationProfile={quotationProfile}
            newCarInvoiceBase64={newCarInvoiceBase64}
            newCarInvoiceFilename={newCarInvoiceFilename}
            setCarInvoiceBase64Action={setCarInvoiceBase64Action}
            invoiceFileStatus={invoiceFileStatus}
          />

          {/* Submit Button */}
          <Grid item xs={12} container justifyContent="center">
            <div className={classes.buttonContainer}>
              <DefaultButton
                id="sales-web-inspection-button-next"
                data-testid="submit-button"
                fullWidth
                type="submit"
                disabled={buttonIsDisabled}
              >
                {requestingUpdateDataStatus === 'requesting' ? (
                  <CircularProgress data-testid="spinner-btn" color="inherit" />
                ) : (
                  'Siguiente'
                )}
              </DefaultButton>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  newCarInvoiceFilename: string;
  newCarInvoiceBase64: string;
  textValues: TextValuesPayload;
  quotationUnits: QuotationUnit[];
  quotationProfile: QuotationProfile;
} => ({
  newCarInvoiceFilename: state.emissionPolicy.newCarInvoiceFilename,
  newCarInvoiceBase64: state.emissionPolicy.newCarInvoiceBase64,
  textValues: state.inspectionForm.textValues,
  quotationUnits: state.quotation.quotation.units,
  quotationProfile: state.quotation.quotation.quotationProfile,
});
const reduxActions = {
  setTextValuesAction: setTextValues,
  setCarInvoiceBase64Action: setCarInvoiceBase64,
  setExclusionDateAction: setExclusionDate,
  requestUpdateEmission: updateEmissionByPage,
  resetRequestStatus: setRequestStatus,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSubmit: () => void;
}

export default connector(NewCarData);
